import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Tree, Input, Icon } from 'antd';
import { TreeProps } from 'antd/lib/tree';
import { TreeNodeNormal } from 'antd/lib/tree/Tree';
import _ from 'lodash';
import { utimes } from 'fs';
import { arrToJsonPrimary } from '@/utils';
import './index.scss';
import { InputProps } from 'antd/lib/input';
const { TreeNode } = Tree;

export interface ISearchAbleTreeProps extends TreeProps {
    otherUse?: boolean;
    selectedKeys?: string[];
    onSelectedChange?: (nextSelected: string[], nodes?: any) => void;
    clearSearch?: boolean;
    inputProps?: InputProps;
}

export interface IMyTreeNodeNormal extends TreeNodeNormal {
    readonly?: boolean;
}

// 递归渲染
const renderTreeNodes = (
    data: IMyTreeNodeNormal[] | undefined,
    visibleKeysMap: { [key: string]: boolean },
) => {
    const allVisible = _.isEmpty(visibleKeysMap);
    const isVisible = (key: string) => {
        return allVisible || visibleKeysMap[key];
    };
    return data
        ? data.map(item => {
              if (item.children) {
                  return (
                      <TreeNode
                          className={
                              isVisible(item.key) ? '' : 'invisible-tree-node'
                          }
                          title={item.title}
                          key={item.key}
                          dataRef={item}
                      >
                          {
                              renderTreeNodes(
                                  item.children,
                                  visibleKeysMap,
                              ) as any
                          }
                      </TreeNode>
                  );
              }
              return (
                  <TreeNode
                      className={
                          isVisible(item.key) ? '' : 'invisible-tree-node'
                      }
                      disabled={!!item.readonly}
                      title={item.title}
                      key={item.key}
                      dataRef={item}
                  />
              );
          })
        : null;
};

const getAllKeysShouldExpands = (data: TreeNodeNormal[] | undefined) => {
    const keys: string[] = [];
    if (!data) {
        return keys;
    }
    const traverse = (d: TreeNodeNormal[]) => {
        for (const item of d) {
            if (item.children) {
                keys.push(item.key);
                traverse(item.children);
            }
        }
    };
    traverse(data);
    return keys;
};

const getAllKeysShouldExpandsBySearch = (
    data: TreeNodeNormal[] | undefined,
    search: string,
) => {
    const result: string[] = [];
    if (search === '' || data === undefined) {
        return result;
    }
    const traverse = (d: TreeNodeNormal[]): string[] => {
        const keys: string[] = [];
        for (const item of d) {
            if (item.children) {
                const innerKeys = traverse(item.children);
                if (innerKeys.length > 0) {
                    keys.push(...innerKeys);
                    keys.push(item.key);
                }
            } else if (_.isString(item.title)) {
                const matched = item.title.indexOf(search) > -1;
                if (matched) {
                    keys.push(item.key);
                }
            }
        }
        return keys;
    };
    result.push(...traverse(data));
    return result;
};

export const SearchAbleTree: React.FC<ISearchAbleTreeProps> = props => {
    const {
        treeData,
        selectedKeys = [],
        onSelectedChange = () => void 0,
        inputProps = {},
        otherUse = false,
        clearSearch,
    } = props;

    const [search, _setSearch] = useState('');
    const setSearch = _.debounce(_setSearch, 200);

    const [userExpandKeys, setUserExpandKeys] = useState<string[]>(
        getAllKeysShouldExpands(treeData),
    );
    const [searchExpandKeys, setSearchExpandKeys] = useState<string[]>(
        getAllKeysShouldExpandsBySearch(treeData, _.trim(search)),
    );
    useEffect(() => {
        if (clearSearch) {
            _setSearch('');
            const firstKey = treeData?.[0]?.key;
            if (firstKey) {
                setUserExpandKeys([firstKey]);
            } else {
                setUserExpandKeys([]);
            }
        }
    }, [clearSearch]);
    useEffect(() => {
        if (otherUse) {
            // 自动展开第一级
            const firstKey = treeData?.[0]?.key;
            if (firstKey) {
                setUserExpandKeys([firstKey]);
            }
        }
    }, [treeData]);
    useLayoutEffect(() => {
        const nextSearchExpandKeys = getAllKeysShouldExpandsBySearch(
            treeData,
            search,
        );
        setSearchExpandKeys(nextSearchExpandKeys);
    }, [treeData, search]);
    const expandedKeys = searchExpandKeys.length
        ? searchExpandKeys
        : userExpandKeys;
    // 空的话就是全部visible
    const visibleKeys = searchExpandKeys.length ? searchExpandKeys : [];
    const visibleKeysMap = arrToJsonPrimary(visibleKeys);

    return (
        <div className={`component-searchable-tree ${otherUse ? 'other' : ''}`}>
            <div className="search">
                <Input
                    key={clearSearch ? '1' : '2'}
                    allowClear
                    onChange={e => {
                        if (e.target) {
                            setSearch(e.target.value);
                        }
                    }}
                    suffix={<Icon type="search" />}
                    {...inputProps}
                ></Input>
            </div>
            <div className="tree">
                <Tree
                    expandedKeys={expandedKeys}
                    onExpand={nextExpandedKeys => {
                        setUserExpandKeys(nextExpandedKeys);
                    }}
                    checkable
                    checkedKeys={selectedKeys}
                    defaultCheckedKeys={[]}
                    onCheck={(keys, e) => {
                        if (_.isArray(keys)) {
                            if (!otherUse) {
                                onSelectedChange(
                                    _.filter(keys, key =>
                                        _.startsWith(key, 'param-'),
                                    ),
                                );
                            } else {
                                onSelectedChange(keys, e.checkedNodes);
                            }
                        }
                    }}
                    multiple
                    {...props}
                >
                    {renderTreeNodes(treeData, visibleKeysMap)}
                </Tree>
            </div>
        </div>
    );
};
